// ─── Cercare un socio, da bordo campo ────────────────────────────────────────
//
// 🔒⭐⭐ **UNA SOLA RICERCA PER TUTTA L'AREA MAESTRO, e non è fattorizzazione
//    per eleganza: è un CANALE.** Di qui passano i nomi dei soci verso un
//    telefono, e il server ne manda **due campi** (⇒ `socioPerMaestroSchema`:
//    un id e un nome). Una seconda schermata che si scrivesse la sua ricerca
//    sarebbe una seconda porta da controllare — e quella dimenticata sarebbe
//    quella nata dopo.
// 📌 *Due strade che leggono le stesse persone divergono al primo campo in più,
//    e la copia dimenticata è quella che nessuno rilegge.*
//
// 🎓 Serve a due gesti che si somigliano e non sono lo stesso: **mettere un
//    allievo in campo** un giorno, e **iscrivere qualcuno a un corso** per tutte
//    le volte che restano. Cambia cosa succede dopo, non come si cerca.
//
// 🆕⭐⭐ **E DAL 20/09/2026 (tappa ⑤) DI QUI PUÒ USCIRE ANCHE UN NOME CHE NON È
//    DI NESSUNO.** Un ospite non si cerca: si **scrive**. ⇒ Il campo di ricerca
//    fa due mestieri — trovare una persona, e comporre un nome — e la cosa che
//    tiene distinti i due gesti è **dove** appare la seconda possibilità: solo
//    quando la ricerca ha risposto «nessun socio», in fondo, con un bordo
//    tratteggiato e parole diverse.
//    ⛔ **Niente secondo campo di testo e niente interruttore socio/ospite**: il
//       nome è già scritto, e chiederlo una seconda volta vorrebbe dire far
//       scrivere due volte la stessa cosa per scegliere cosa ne esce.
//    📌 *Un gesto in più per dire ciò che si è già detto non è una conferma: è
//       un modo di sbagliare due campi invece di uno.*

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';

export interface SocioTrovato {
  id: string;
  displayName: string;
}

/**
 * 🆕 Cosa si fa con un nome che **non è di nessun socio** (tappa ⑤).
 *
 * ⚖️ **È una scelta fra due, non due interruttori**, e il tipo lo dice: o di
 * qui un ospite si può mettere in campo, o non si può **e c'è un motivo da
 * leggere**. Due booleani indipendenti avrebbero ammesso lo stato «non si può,
 * e non si sa perché», che a schermo è un vicolo cieco muto.
 *
 * ⛔ E `undefined` è il terzo caso, quello dell'**iscrizione a un corso**: là
 *    non si nega niente, perché di ospiti non si parla proprio.
 */
export type SeNonSonoSoci =
  | { fai: (nome: string) => Promise<unknown> }
  /* ⚠️ Un pezzo di testo e non una stringa: la frase del corso porta una parola
     in grassetto — **iscritti** — ed è quella che dice dove va a finire chi
     entra in un corso. Ridurla a testo piatto qui vorrebbe dire perderla. */
  | { niente: React.ReactNode };

/**
 * 🔎 Cercare un socio per nome.
 *
 * ⏳ **L'attesa prima di chiedere è mezzo secondo**, e non è un dettaglio di
 * prestazioni: a bordo campo si scrive con un pollice, e una richiesta per
 * lettera manderebbe sei domande per «Bianchi» — su una rete mobile le risposte
 * tornano fuori ordine, e l'elenco lampeggia mostrando i risultati di tre
 * lettere fa.
 *
 * 🔒 Il maestro non trova **sé stesso**, e non perché questa schermata lo
 * tolga: non lo rende il server (⇒ `modules/maestro/deposito.ts`). Qui non c'è
 * nessun filtro da dimenticare.
 */
export function CercaSocio({
  titolo,
  giaDentro,
  comeCiSta,
  scegli,
  ospiti,
  inCorso,
  errore,
}: {
  titolo: string;
  /** I nomi di chi c'è già: si vedono, spenti, **con scritto perché**. */
  giaDentro: string[];
  /** Come si dice che uno c'è già: «già in campo», «già iscritto». */
  comeCiSta: string;
  scegli: (s: SocioTrovato) => void;
  /** 🆕 Assente = di qui non si parla di ospiti. ⇒ `SeNonSonoSoci`. */
  ospiti?: SeNonSonoSoci;
  inCorso: boolean;
  errore: unknown;
}): React.JSX.Element {
  const [scritto, setScritto] = useState('');
  const [cercato, setCercato] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setCercato(scritto.trim()), 500);
    return () => clearTimeout(t);
  }, [scritto]);

  /**
   * 🚨⭐⭐ **SVUOTARE NON È CERCARE, e per questo non aspetta il mezzo secondo.**
   *
   * 👁️ **Misurato sul browser del banco, non pensato** (20/09): toccando il
   * bottone dell'ospite il campo si svuotava, ma `cercato` restava fermo sul
   * nome vecchio per i 500 ms dell'attesa — e in quei 500 ms il riquadro
   * continuava a dire «nessun socio» e a offrire *«Metti in campo «Gigi
   * Ospite»»*, **riacceso** appena la scrittura era andata. Un secondo tocco lì
   * dentro sono **due ospiti**, e nessuno li ferma: due nomi uguali il circolo
   * li ammette apposta.
   *
   * ⚖️ L'attesa esiste per non mandare una domanda per lettera. Un campo
   * svuotato **non manda nessuna domanda** (`enabled` vuole due lettere) ⇒
   * ritardarlo non risparmia niente e costa esattamente quella finestra.
   * 📌 *Un ritardo messo a difendere il server difende anche ciò che il server
   *    non avrebbe mai visto — e lì non difende: nasconde.*
   */
  const svuota = (): void => {
    setScritto('');
    setCercato('');
  };

  /**
   * 📱⭐⭐ **LA CASELLA SALE IN CIMA QUANDO LA SI TOCCA** (23/09/2026).
   *
   * 🗣️ Guardando l'anteprima dal telefono: *«quando aggiungo un allievo viene
   * visualizzato sotto il campo dove scrivo […] e su cellulare non vedo
   * l'elenco che mi appare»*. La ricerca sta in fondo alla scheda: toccandola
   * sale la tastiera, e l'elenco dei nomi — che nasce proprio sotto la casella
   * — finisce **sotto la tastiera**.
   * ⇒ Al tocco la sezione scorre fino in cima (sotto la testata), e l'elenco
   *   cresce nello spazio fra la casella e la tastiera.
   * ⚠️ Due cose che servono perché lo scorrimento riesca davvero:
   *   · **un posto in fondo** (`posto-tastiera`) finché si cerca: la sezione
   *     sta quasi in fondo alla pagina, e una pagina troppo corta non può
   *     portarla in cima — 📏 lo stesso difetto misurato il 23/09 sulle assenze;
   *   · **riscorrere quando la tastiera ha finito di salire**
   *     (`visualViewport`): mentre sale, il telefono sposta la pagina per conto
   *     suo per tenere in vista la casella, e il primo scorrimento andrebbe
   *     perso.
   */
  const sezione = useRef<HTMLElement>(null);
  const [aperta, setAperta] = useState(false);
  const inCima = (): void => sezione.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  useEffect(() => {
    if (!aperta) return;
    const t = setTimeout(inCima, 250);
    const vv = window.visualViewport;
    const rifai = (): void => {
      if (document.activeElement?.closest('.cerca-socio') === sezione.current) inCima();
    };
    vv?.addEventListener('resize', rifai);
    return () => {
      clearTimeout(t);
      vv?.removeEventListener('resize', rifai);
    };
  }, [aperta]);

  const trovati = useQuery({
    queryKey: ['maestro', 'soci', cercato],
    queryFn: () => chiama<{ items: SocioTrovato[] }>('GET', `/maestro/soci?q=${encodeURIComponent(cercato)}`),
    enabled: cercato.length >= 2,
  });

  return (
    <>
      <section className="riquadro cerca-socio" ref={sezione}>
        <h2>{titolo}</h2>
        <label className="campo">
          <span>Cerca per nome</span>
          <input
            value={scritto}
            onChange={(e) => setScritto(e.target.value)}
            onFocus={() => setAperta(true)}
            /* Lasciata vuota, la casella non ha più bisogno del posto in fondo. */
            onBlur={() => {
              if (scritto.trim() === '') setAperta(false);
            }}
            placeholder="Bianchi"
            autoComplete="off"
            /* 🚨 `enterKeyHint` e nient'altro: niente invio che manda un modulo —
             qui non c'è niente da inviare, si tocca il nome che si cerca. */
            enterKeyHint="search"
            /* 🆕⭐ **120 è il limite del SERVER**, non una preferenza di questa
             schermata: `metteInCampoSchema` rifiuta un nome più lungo (ed è lo
             stesso limite della segreteria, apposta). ⇒ Fermarlo qui vuol dire
             che il bottone dell'ospite non può proporre una cosa che verrebbe
             rifiutata. Per la ricerca di un socio non toglie niente: nessun
             nome del circolo ci arriva vicino. */
            maxLength={120}
          />
        </label>

        {cercato.length < 2 ? (
          <p className="tenue piccolo">Bastano due lettere.</p>
        ) : trovati.isLoading ? (
          <Attesa cosa="i soci" />
        ) : trovati.error ? (
          <Guasto errore={trovati.error} />
        ) : (trovati.data?.items.length ?? 0) === 0 ? (
          /* 🆕⭐⭐ **QUI, E SOLO QUI.** L'ospite si propone dove la ricerca ha
           appena detto «questo nome non è di nessuno»: è l'unico momento in cui
           comporre un nome non fa concorrenza al riconoscerne uno.
           👁️ **Misurato sul disegno, non pensato**: col bottone sempre acceso,
              scrivendo «Mar» si leggeva *«Metti in campo «Mar»»* sotto due soci
              veri — cioè l'offerta di inventare un ospite che si chiama **Mar**,
              a un dito di distanza dalla persona che si stava cercando.
           ⚖️ Il caso che questa scelta taglia fuori — un ospite che si chiama
              **come** un socio — ha una via d'uscita: si scrive il nome per
              intero, e siccome nessun socio lo porta identico la ricerca torna
              vuota. Un caso raro con una strada, invece di un caso frequente
              con una trappola. */
          <>
            <p className="tenue">Nessun socio con questo nome.</p>
            {ospiti !== undefined &&
              ('fai' in ospiti ? (
                <div className="fai-ospite">
                  <button
                    type="button"
                    disabled={inCorso}
                    /* ⭐ Si offre il nome **cercato**, non quello scritto: «nessun
                     socio» è una risposta su `cercato`, e proporre l'altro
                     vorrebbe dire creare un ospite con un nome su cui nessuno
                     ha mai guardato. Per mezzo secondo i due differiscono, e in
                     quel mezzo secondo tutto il riquadro è d'accordo con sé
                     stesso perché guarda la stessa parola. */
                    onClick={() => {
                      /* 🚨⭐ **IL CAMPO SI SVUOTA QUANDO È ANDATA, non quando si
                       è toccato.** Svuotarlo subito toglierebbe il nome da sotto
                       il messaggio di rifiuto — e il rifiuto vero esiste: su una
                       volta di corso il server risponde `OSPITE_FUORI_DA_UNA_
                       LEZIONE`. Lasciarlo pieno **dopo** invece farebbe toccare
                       due volte, e un ospite toccato due volte sono due ospiti:
                       il circolo ammette due nomi uguali apposta, quindi non
                       c'è niente che lo fermi. ⇒ `svuota` e non `setScritto`:
                       il perché sta lassù, e l'ha trovato il browser.
                       ⚠️ Il `catch` non nasconde niente: il guasto si legge qui
                          sotto, da `errore`, che è lo stato della mutazione. */
                      void ospiti
                        .fai(cercato)
                        .then(svuota)
                        .catch(() => {});
                    }}
                  >
                    <strong>Metti in campo «{cercato}»</strong>
                    <small>come ospite — non è un socio del circolo</small>
                  </button>
                </div>
              ) : (
                /* ⚠️ Il divieto si legge **solo quando serve**, cioè quando un
                 nome che non è di nessuno è già stato scritto: dirlo sempre
                 sarebbe spiegare una cosa che non si stava facendo. */
                <p className="regola">{ospiti.niente}</p>
              ))}
          </>
        ) : (
          <ul className="elenco-trovati">
            {trovati.data!.items.map((s) => (
              <li key={s.id}>
                {/* ⚠️ Chi è già in campo si vede **e si spiega**: un nome grigio che
                  non risponde al tocco fa credere a un guasto, e da bordo campo
                  la reazione è premerlo più forte. */}
                <button
                  type="button"
                  className="largo"
                  disabled={inCorso || giaDentro.includes(s.displayName)}
                  onClick={() => scegli(s)}
                >
                  {s.displayName}
                  {giaDentro.includes(s.displayName) && <span className="tenue"> · {comeCiSta}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* ⚠️ Il rifiuto si legge QUI, sotto il bottone che si è premuto: un
          messaggio in cima alla pagina, su un telefono, sta fuori schermo. */}
        <Guasto errore={errore} />
      </section>
      {/* Fuori dal riquadro, e trasparente: dentro, il riquadro bianco si
        allungherebbe di mezza pagina vuota. */}
      {aperta && <div className="posto-tastiera" aria-hidden="true" />}
    </>
  );
}
